import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Chip,
  Stack,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import { ThaiStar, ThaiQuatrefoil, ThaiNestedMotif, ThaiDivider } from './ThaiMotif';

interface ExperienceItem {
  company: string;
  role: string;
  period: string;
  type: string;
  description: string[];
  projects: {
    name: string;
    details: string[];
  }[];
  techStack: string[];
}

const experiences: ExperienceItem[] = [
  {
    company: 'Energy Absolute',
    role: 'Team Lead, Full-stack Software Engineer',
    period: 'Jan 2023 - Present',
    type: 'Full-Time',
    description: [
      'Overseeing the full product lifecycle of TSB Go Plus — from system architecture and design through development, deployment, monitoring, and ongoing maintenance',
      'Led end-to-end delivery of a cross-platform transit app serving 50,000+ daily active users across Bangkok\'s bus network (142 routes, 2,865+ stops)',
      'Mentored junior developers, collaborated cross-functionally with vendors and stakeholders, and drove key technical decisions for the team',
    ],
    projects: [
      {
        name: 'TSB Go Plus — Bangkok Transit Super App',
        details: [
          'Built a cross-platform mobile app (Flutter/Dart) that helps commuters navigate Thai Smile Bus routes across Bangkok and the greater metropolitan area',
          'Implemented real-time vehicle tracking with live bus locations and Estimated Time of Arrival (ETA) predictions on an interactive map',
          'Developed an intelligent route optimization engine suggesting optimal paths, transfers, and nearby stops for unfamiliar commuters',
          'Integrated HOP Card NFC functionality — enabling contactless payments, balance top-ups, and travel history viewing directly from the app',
          'Built a Geofencing service using ray tracing algorithms for location-aware notifications and zone-based features',
          'Designed data-optimized datasets and built ETL/ELT pipelines for real-time GPS data processing at scale',
          'Developed a high-performance POI Search Engine powered by Typesense for fuzzy, instant point-of-interest searches across Bangkok\'s transit network',
          'Engineered backend microservices in Golang with hexagonal architecture, supporting real-time data from 2,865+ bus stops',
        ],
      }
    ],
    techStack: ['Golang', 'Flutter/Dart', 'React', 'TypeScript', 'Redis', 'PostgreSQL', 'Docker', 'GCP', 'Kubernetes', 'gRPC'],
  },
  {
    company: 'Energy Absolute',
    role: 'Data Scientist',
    period: 'July 2022 - Jan 2023',
    type: 'Full-Time',
    description: [
      'Drove the full machine learning lifecycle — from surveying research papers and benchmarking state-of-the-art models to adapting architectures for production use cases',
      'Conducted extensive model comparison experiments across multiple deep learning approaches, evaluating trade-offs between accuracy, latency, and scalability',
      'Designed and deployed end-to-end MLOps pipelines covering data collection, validation, training, inference, monitoring, and continuous retraining',
    ],
    projects: [
      {
        name: 'Estimated Time of Arrival (ETA) Prediction',
        details: [
          'Researched and implemented Spatial-Temporal Graph Neural Networks (ST-GNNs) for bus travel time prediction, adapting cutting-edge papers to real-world Bangkok traffic patterns',
          'Built robust GPS data pipelines for Thai Smile Bus fleet — ingesting, cleaning, and validating millions of data points',
          'Transformed raw GPS traces into graph structures modeling bus routes, stops, and traffic segments for accurate travel time modeling',
          'Compared multiple model architectures (GCN, GAT, STGCN) and tuned hyperparameters to achieve optimal prediction accuracy for the Bangkok transit network',
          'Tracked all experiments and model versions systematically using DVC and MLFlow for full reproducibility',
        ],
      },
      {
        name: 'MLOps & Production Pipeline',
        details: [
          'Designed and built automated inference and continuous training pipelines using Apache Airflow, enabling scheduled model retraining on fresh data',
          'Implemented real-time model health monitoring with Prometheus alerts for data drift and performance degradation',
          'Visualized model metrics, pipeline health, and prediction quality dashboards using Grafana',
        ],
      },
    ],
    techStack: ['Python', 'PyTorch', 'Airflow', 'Prometheus', 'Grafana', 'DVC', 'MLFlow', 'TensorFlow', 'sklearn'],
  },
  {
    company: 'Shopee',
    role: 'Business Intelligence',
    period: 'June 2021 - June 2022',
    type: 'Full-Time',
    description: [
      'Built and maintained automated web scraping systems to monitor competitor pricing, promotions, and product strategies — supplying actionable intelligence to the business team for creating targeted promotions, discount vouchers, and competitive campaigns',
      'Performed advanced data analytics including time series forecasting for order completions, customer lifetime value modeling, and seasonal trend analysis',
      'Conducted deep exploratory data analysis (SQL/Python) and customer segmentation using clustering algorithms (K-Means, DBSCAN, Mean-Shift) to identify high-value segments and optimize marketing spend',
      'Designed and automated end-to-end data pipelines for real-time sales reporting and business KPI dashboards',
    ],
    projects: [],
    techStack: ['Python', 'SQL', 'pySpark', 'Selenium', 'pandas', 'sklearn', 'BeautifulSoup'],
  },
];

const ExperienceCard: React.FC<{ exp: ExperienceItem; index: number }> = ({ exp, index }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card
      className="card"
      sx={{
        p: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  background: '#C8A24D',
                  transform: 'rotate(45deg)',
                  flexShrink: 0,
                }}
              />
              <Typography variant="h5" sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#2A231C' }}>
                {exp.role}
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: '#C8A24D',
                fontWeight: 500,
                fontSize: '0.95rem',
                ml: 3,
              }}
            >
              {exp.company}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#9CA3AF', mt: 0.3, ml: 3, fontSize: '0.85rem' }}
            >
              {exp.period} · {exp.type}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ color: '#9CA3AF', mt: 0.5 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 }, pt: 0 }}>
          {/* Description */}
          <Box sx={{ mb: 3 }}>
            {exp.description.map((desc, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1, ml: 1 }}>
                <CircleIcon sx={{ fontSize: 5, color: '#D1D5DB', mt: 1.2 }} />
                <Typography variant="body2" sx={{ color: '#7A6E60', fontSize: '0.9rem' }}>
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Projects */}
          {exp.projects.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {exp.projects.map((project, i) => (
                <Box
                  key={i}
                  sx={{
                    ml: 1,
                    mb: 2,
                    pl: 2.5,
                    borderLeft: '2px solid #C8A24D',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#5C3D2E',
                      fontWeight: 600,
                      mb: 1,
                      fontSize: '0.9rem',
                    }}
                  >
                    {project.name}
                  </Typography>
                  {project.details.map((detail, j) => (
                    <Box key={j} sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
                      <CircleIcon sx={{ fontSize: 4, color: '#D1D5DB', mt: 1.1 }} />
                      <Typography variant="body2" sx={{ color: '#7A6E60', fontSize: '0.85rem' }}>
                        {detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {/* Tech stack */}
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {exp.techStack.map((tech, i) => (
              <Chip
                key={i}
                label={tech}
                size="small"
                sx={{
                  background: 'rgba(200, 162, 77, 0.08)',
                  color: '#B08E3A',
                  border: '1px solid rgba(200, 162, 77, 0.15)',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Card>
  );
};

const Experience: React.FC = () => {
  return (
    <Box
      id="experience"
      sx={{
        py: { xs: 10, md: 14 },
        background: '#FAF6EE',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Thai motif decorations */}
      <ThaiNestedMotif size={130} opacity={0.05} innerColor="#FAF6EE" sx={{ top: '3%', left: '2%' }} />
      <ThaiStar size={70} opacity={0.035} rotate={20} sx={{ bottom: '5%', right: '4%' }} />
      <ThaiQuatrefoil size={55} opacity={0.03} sx={{ top: '50%', right: '2%' }} />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#C8A24D',
              fontWeight: 600,
              letterSpacing: 3,
              fontSize: '0.8rem',
              mb: 1.5,
              display: 'block',
            }}
          >
            EXPERIENCE
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#2A231C' }}
          >
            Where I've{' '}
            <Box component="span" className="serif-heading" sx={{ color: '#C8A24D' }}>
              Worked
            </Box>
          </Typography>
          <ThaiDivider />
        </Box>

        <Stack spacing={3}>
          {experiences.map((exp, index) => (
            <ExperienceCard key={index} exp={exp} index={index} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Experience;
